import { z } from 'zod';
import { WireRulesSchema } from './rules.js';
/**
 * Wire protocol (DESIGN-server §3.2). Client→server messages are validated with these schemas
 * on the room; server→client messages are typed here for the client driver.
 */
export const SeatSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
export const CardIdSchema = z.number().int().min(0).max(53);
export const BidSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('tricks'), n: z.number().int().min(1).max(13) }),
    z.object({ kind: z.literal('nil') }),
    z.object({ kind: z.literal('blindNil') }),
]);
/** Names of client→server messages. */
export const clientMessages = z.object({
    sit: z.object({ seat: SeatSchema.optional() }),
    standUp: z.object({}),
    setRules: z.object({ rules: WireRulesSchema }), // host, lobby
    start: z.object({}), // host
    peek: z.object({}),
    // `slam` is presentation only, passed straight through to the `bidLocked` cue so every client
    // can play the slam effect. The engine never sees it, exactly like `play.slam`.
    bid: z.object({ bid: BidSchema, slam: z.number().int().min(0).max(3).optional() }),
    lockCard: z.object({ card: CardIdSchema.nullable(), slam: z.boolean().optional() }),
    play: z.object({ card: CardIdSchema, slam: z.number().int().min(0).max(3).optional() }),
    timeBank: z.object({}),
    resync: z.object({ sinceSeq: z.number().int().min(0) }),
    rematchVote: z.object({ choice: z.enum(['rematch', 'money', 'leave']) }),
    emote: z.object({ id: z.string().min(1).max(32) }),
    addBot: z.object({ seat: SeatSchema }), // host, lobby
    kickBot: z.object({ seat: SeatSchema }), // host, lobby
});
export const ClientMessageSchemas = clientMessages.shape;
export const clientMessageTypes = Object.keys(ClientMessageSchemas);
export function parseClientMessage(type, payload) {
    return ClientMessageSchemas[type].parse(payload);
}
export function safeParseClientMessage(type, payload) {
    const r = ClientMessageSchemas[type].safeParse(payload);
    return r.success ? { success: true, data: r.data } : { success: false, error: r.error };
}
/** Server→client message names (besides Colyseus schema patches). */
// `emote` is NOT here: an emote reaches the client as a `cue`, which carries a seq, orders against
// the rest of the stream and survives a resync. It was also broadcast as its own message, and the
// client turned that into a second cue, so every sticker and its sound played twice.
export const serverMessageTypes = ['cue', 'private', 'cues', 'error', 'sitPending'];
/**
 * `onCreate(options)` for the table room (DESIGN-server §3.2/§3.3).
 *
 * The id fields are `nullish`, not `optional`: the Discord bridge reports an unknown guild or
 * channel as `null` rather than by leaving the key out, and a rejected parse here costs the room
 * its surface and its instance id.
 */
export const CreateOptionsSchema = z.object({
    surface: z.enum(['discord', 'web']).default('web'),
    instanceId: z.string().nullish(),
    guildId: z.string().nullish(),
    channelId: z.string().nullish(),
    code: z.string().nullish(),
    solo: z.boolean().nullish(),
    private: z.boolean().nullish(),
    rules: WireRulesSchema.optional(),
    hostPlayerId: z.string().nullish(),
});
/** `onJoin(client, options)` for the table room. Same `nullish` reasoning as `CreateOptionsSchema`. */
export const JoinOptionsSchema = z.object({
    surface: z.enum(['discord', 'web']).default('web'),
    instanceId: z.string().nullish(),
    guildId: z.string().nullish(),
    channelId: z.string().nullish(),
    /** Client-side reconnection token, when re-entering after a socket drop. */
    reconnectionToken: z.string().nullish(),
    /** Seat intent; honored at the next hand boundary if the table is mid-hand. */
    seat: SeatSchema.nullish(),
});
/** `POST /api/rooms` body and response; `GET /api/rooms/:code` response. */
export const CreateRoomRequestSchema = z.object({
    rules: WireRulesSchema.optional(),
    private: z.boolean().optional(),
    solo: z.boolean().optional(),
});
//# sourceMappingURL=protocol.js.map